import { Avatar, Flex, Typography, theme } from 'antd';
import { useState } from 'react';
import { ChartTooltipWrapperEcho } from '@/components/charts/ChartTooltipEcho';
import { formatPercentageTwoDp } from '@/utils/numberFormattingUtils';
import { themeRawValues } from '@/utils/styleUtils';
import { DevicesPercentageListTooltipContent } from './DevicesPercentageListTooltipContent';
import { TopPlayersPercentageListTooltipContent } from './TopPlayersPercentageListTooltipContent';
import { getDisplayLabel, TOP_PLAYER_CATEGORY } from '../topPlayerCategories';

const LABEL_WIDTH = '8rem';
const PERCENTAGE_VALUE_WIDTH = '3rem';
const PLAYS_VALUE_WIDTH = '5rem';
const BAR_HEIGHT = 8;
const LEGEND_SWATCH_SIZE = 12;
const TOOLTIP_WIDTH = 256;

const getDisplayCategoryForLegend = (category: string) => {
  switch (category) {
    case TOP_PLAYER_CATEGORY.podcastPlayerApps:
    case TOP_PLAYER_CATEGORY.webPodcastPlayers:
      return 'Podcast players';
    default:
      return category;
  }
};

export const PercentagesListEcho = ({
  data,
  legend,
  legendLabels,
  colors,
  usePercentage = true,
  renderTooltipContent,
}: {
  data: Array<{
    label: string | null;
    values: Array<{ label: string; value: number }>;
    total: number;
    plays: number;
  }>;
  legend?: Array<{ category: string; description: string }>;
  legendLabels?: Record<string, string>;
  colors: Record<string, string>;
  usePercentage?: boolean;
  renderTooltipContent?:
    | typeof TopPlayersPercentageListTooltipContent
    | typeof DevicesPercentageListTooltipContent;
}) => {
  const { token } = theme.useToken();

  return (
    <Flex vertical gap={token.marginLG}>
      <Flex
        vertical
        component="ul"
        gap={token.marginXS}
        style={{ listStyle: 'none', margin: 0, padding: 0 }}
      >
        {data.map((item, itemIndex) => {
          const displayLabel = getDisplayLabel(item.label);

          return (
            <Flex
              key={item.label ?? `unknown-${itemIndex}`}
              component="li"
              align="center"
              gap={token.marginXS}
            >
              <Typography.Text
                ellipsis
                style={{
                  width: LABEL_WIDTH,
                  fontSize: token.fontSizeSM,
                  fontWeight: 500,
                  textAlign: 'right',
                }}
              >
                {displayLabel}
              </Typography.Text>
              <Flex flex={1} style={{ minWidth: 0 }}>
                <ProgressMultiBar
                  label={displayLabel}
                  percentages={item.values}
                  total={item.total}
                  plays={item.plays}
                  colors={colors}
                  renderTooltipContent={renderTooltipContent}
                />
              </Flex>
              <Typography.Text
                style={{
                  width: usePercentage ? PERCENTAGE_VALUE_WIDTH : PLAYS_VALUE_WIDTH,
                  fontSize: token.fontSizeSM,
                  fontWeight: 500,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                }}
              >
                {usePercentage
                  ? `${formatPercentageTwoDp(item.total)}%`
                  : item.plays.toLocaleString()}
              </Typography.Text>
            </Flex>
          );
        })}
      </Flex>
      {legend && (
        <Flex justify="center" gap={token.margin}>
          {legend.map((item) => (
            <Flex key={item.category} align="center" gap={token.marginXS}>
              <Avatar
                size={LEGEND_SWATCH_SIZE}
                style={{
                  backgroundColor: colors[item.category] ?? themeRawValues.colors.textMedGrey,
                }}
              />
              <Typography.Text
                style={{
                  color: token.colorTextSecondary,
                  fontSize: token.fontSizeSM,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {legendLabels?.[item.category] ?? getDisplayCategoryForLegend(item.category)}
                <br />
                {item.description && (
                  <Typography.Text
                    style={{
                      color: token.colorTextSecondary,
                      fontSize: token.fontSizeSM,
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.description}
                  </Typography.Text>
                )}
              </Typography.Text>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

const ProgressMultiBar = ({
  label,
  percentages = [],
  total,
  plays,
  colors,
  renderTooltipContent,
}: {
  label: string;
  percentages: Array<{ label: string; value: number }>;
  total: number;
  plays: number;
  colors: Record<string, string>;
  renderTooltipContent?:
    | typeof TopPlayersPercentageListTooltipContent
    | typeof DevicesPercentageListTooltipContent
    | null;
}) => {
  const { token } = theme.useToken();
  const [tooltip, setTooltip] = useState<{
    label: string;
    value: number;
    x: number;
  } | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;

    let accumulatedWidth = 0;
    const containerWidth = rect.width;

    const hoveredSegment = percentages.find(() => {
      const segWidth = containerWidth;
      accumulatedWidth += segWidth;
      return offsetX <= accumulatedWidth;
    });

    if (hoveredSegment && renderTooltipContent !== null) {
      setTooltip({
        label: hoveredSegment.label,
        value: hoveredSegment.value,
        x: offsetX,
      });
    } else {
      setTooltip(null);
    }
  };

  return (
    <Flex align="center" style={{ position: 'relative', width: '100%' }}>
      <Flex
        role="progressbar"
        aria-valuenow={total}
        aria-valuemin={0}
        aria-valuemax={100}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
        style={{
          height: BAR_HEIGHT,
          minWidth: 0,
          width: '100%',
          marginBlock: token.marginXXS,
          overflow: 'hidden',
          flexShrink: 0,
          borderRadius: token.borderRadiusLG,
          background: token.colorPrimaryBg,
        }}
      >
        {percentages.map((percentage, segmentIndex) => (
          <span
            key={percentage.label}
            style={{
              display: 'block',
              flexShrink: 0,
              height: BAR_HEIGHT,
              width: `${percentage.value}%`,
              backgroundColor: colors?.[percentage.label] ?? themeRawValues.colors.textMedGrey,
              ...(segmentIndex === 0 && {
                borderTopLeftRadius: token.borderRadiusLG,
                borderBottomLeftRadius: token.borderRadiusLG,
              }),
              ...(segmentIndex === percentages.length - 1 && {
                borderTopRightRadius: token.borderRadiusLG,
                borderBottomRightRadius: token.borderRadiusLG,
              }),
            }}
          />
        ))}
      </Flex>
      {tooltip && renderTooltipContent && (
        <Flex
          style={{
            position: 'absolute',
            zIndex: token.zIndexPopupBase,
            pointerEvents: 'none',
            left: tooltip.x,
            bottom: '100%',
            transform: 'translateX(-50%) translateY(-8px)',
            width: TOOLTIP_WIDTH,
          }}
        >
          <ChartTooltipWrapperEcho>
            {(() => {
              const TooltipComponent = renderTooltipContent;
              return (
                <TooltipComponent
                  label={label}
                  value={total}
                  plays={plays ?? 0}
                  percentages={percentages}
                />
              );
            })()}
          </ChartTooltipWrapperEcho>
        </Flex>
      )}
    </Flex>
  );
};
